import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FeedbackAPI } from "../../services/api";
import ClosurePickerMap from "../../components/ClosurePickerMap";

const CLOSED_ROUTE_ID = 3;

export default function UserFeedback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [active, setActive] = useState(null);
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  // ตำแหน่งทางปิดที่ user เลือก (ใช้เฉพาะ category "ทางปิด")
  const [closure, setClosure] = useState(null); // {floorId,x,y,floorName,buildingName}
  const [pickerOpen, setPickerOpen] = useState(false);

  const problems = [
    { id: 1, icon: "📍", label: t("user.feedback.problems.locationMismatch") },
    { id: 2, icon: "🏠", label: t("user.feedback.problems.roomMoved") },
    { id: CLOSED_ROUTE_ID, icon: "🚫", label: t("user.feedback.problems.closedRoute") },
    { id: 4, icon: "•••", label: t("user.feedback.problems.other") },
  ];

  const isClosedRoute = active === CLOSED_ROUTE_ID;

  const onSubmit = async () => {
    setErrMsg("");
    const cat = problems.find((p) => p.id === active)?.label;
    if (!cat) {
      setErrMsg(t("user.feedback.selectProblemError"));
      return;
    }
    if (isClosedRoute && !closure) {
      setErrMsg("กรุณาเลือกตำแหน่งทางปิดบนแผนที่");
      return;
    }
    setSubmitting(true);
    try {
      await FeedbackAPI.submit({
        category: cat,
        description,
        image: imageFile,
        // ถ้าเป็น "ทางปิด" แนบ closure เพื่อให้ backend สร้าง path_closure(active) ผูกกับ feedback
        ...(isClosedRoute && closure
          ? {
              closure: {
                floorId: closure.floorId,
                x: closure.x,
                y: closure.y,
                radiusPx: 80, // ขยายรัศมีกัน edge ที่ตัดผ่านจุดถูกตรวจไม่พบ
              },
            }
          : {}),
      });
      setIsSubmitted(true);
    } catch (e) {
      setErrMsg(e.message || t("user.feedback.submitFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const mockfeedback = {
    id: 1,
    img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSAXN_IhdXPhdB8jCsg9MT3x9rBYtIwg3tmlw&s",
    text: "รพ.มหาราชนครเชียงใหม่ ปรับปรุงระบบลิฟต์ใหม่",
  };

  const handleClose = () => {
    navigate("/user/mainpage");
  };

  // Render submission success view if submitted successfully
  if (isSubmitted) {
    return (
      <div className="w-full h-screen bg-white flex flex-col items-center mt-32">
        <div className="fixed top-0 left-0 w-full p-4">
          <span 
            className="absolute left-5 font-bold text-slate-500 text-xl cursor-pointer"
            onClick={handleClose}
          >
            ✕
          </span>
        </div>

        <div className="relative flex items-center justify-center">
          <div className="w-80 h-80 rounded-full bg-sky-50"></div>
          <div className="absolute w-64 h-64 rounded-full bg-sky-100"></div>
          <div className="absolute w-36 h-36 rounded-3xl bg-white"></div>
          <div className="absolute w-24 h-24 rounded-2xl bg-green-500"></div>
          <div className="absolute w-12 h-12 rounded-full bg-white"></div>
          <span className="absolute text-center text-green-500 text-4xl font-bold">
            ✓
          </span>
        </div>

        <p className="text-2xl font-bold mt-4">{t("user.feedback.successTitle")}</p>
        <p className="text-xl mt-4 text-blue-500">{t("user.feedback.successThanks")}</p>
        <p className="text-base mt-2">{t("user.feedback.successDetail1")}</p>
        <p className="text-base ">{t("user.feedback.successDetail2")}</p>

        <div className="fixed bottom-0 left-0 w-full p-4">
          <button 
            onClick={handleClose}
            className="w-full bg-blue-600 text-white py-5 rounded-xl font-bold text-base shadow-md"
          >
            <span className="mr-2">🗺️</span>{t("user.feedback.backToMap")}
          </button>
        </div>
      </div>
    );
  }

  // Render default feedback form view
  return (
    <div className="relative w-full min-h-screen bg-slate-50 overflow-hidden">
      <div className="items-center w-full">
        <div className="flex min-w-0 items-center bg-white w-full py-5 relative">
          <span 
            className="absolute left-5 font-bold text-slate-400 text-xl cursor-pointer"
            onClick={handleClose}
          >
            ✕
          </span>
          <p className="w-full text-center text-base font-semibold">
            {t("user.feedback.title")}
          </p>
        </div>
      </div>

      <p className="w-full ml-4 mt-3 text-base font-semibold">
        {t("user.feedback.selectProblem")}
      </p>

      <div className="grid grid-cols-2 gap-3 m-4">
        {problems.map((item) => (
          <button
            key={item.id}
            onClick={() => setActive(item.id)}
            className={`flex-1 rounded-xl bg-white shadow flex flex-col items-center justify-center py-6 transition-all duration-200 border-2 ${
              active === item.id ? "border-blue-600" : "border-transparent"
            }`}
          >
            <span className="bg-blue-100 rounded-full w-14 h-14 flex items-center justify-center text-xl leading-none">
              {item.icon}
            </span>

            <p className="text-base font-bold mt-3 text-center">{item.label}</p>
          </button>
        ))}
      </div>

      {/* ===== ทางปิด: ปุ่มเลือกตำแหน่งบนแผนที่ ===== */}
      {isClosedRoute && (
        <div className="mx-4 mt-2 mb-1">
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className={`w-full rounded-xl border-2 ${
              closure
                ? "border-red-300 bg-red-50"
                : "border-dashed border-red-300 bg-white"
            } p-3 text-left flex items-center gap-3`}
          >
            <span className="text-2xl">🚧</span>
            <div className="flex-1 min-w-0">
              {closure ? (
                <>
                  <div className="font-bold text-red-700 text-sm">
                    เลือกตำแหน่งแล้ว
                  </div>
                  <div className="text-xs text-slate-600 truncate">
                    {closure.buildingName} • {closure.floorName} (
                    {Math.round(closure.x)}, {Math.round(closure.y)})
                  </div>
                </>
              ) : (
                <>
                  <div className="font-bold text-red-700 text-sm">
                    เลือกตำแหน่งทางปิดบนแผนที่
                  </div>
                  <div className="text-xs text-slate-500">
                    แตะปุ่มนี้แล้วจิ้มจุดที่ทางปิด
                  </div>
                </>
              )}
            </div>
            <span className="text-red-600 font-bold">
              {closure ? "แก้ไข" : "เลือก ›"}
            </span>
          </button>
        </div>
      )}

      <p className="w-full ml-4 mt-3 text-base font-bold">
        {t("user.feedback.attachImageOptional")}
      </p>
      <div className="m-4">
        <div
          className="relative w-full h-48 rounded-xl overflow-hidden bg-cover bg-center"
          style={{
            backgroundImage: `url(${mockfeedback.img})`,
          }}
        >
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
            <label className="flex items-center justify-center gap-3 bg-white/80 px-20 sm:px-10 py-2 rounded-xl font-bold text-blue-600 cursor-pointer w-max">
              <span className="mr-1">📷</span>
              {t("user.feedback.attachImage")}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              />
            </label>
          </div>
        </div>
      </div>
      <p className="ml-4 mt-3 text-base font-bold">{t("user.feedback.additionalDetail")}</p>
      <div className="mt-3 mr-4 ml-4 rounded-xl border border-slate-300 bg-white">
        <textarea
          className="w-full min-h-[100px] rounded-xl p-4 text-sm text-slate-700 placeholder:text-slate-400 outline-none resize-none"
          placeholder={mockfeedback.text}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      {errMsg && (
        <div className="mx-4 mt-2 text-sm text-red-600">{errMsg}</div>
      )}

      <div className="flex items-center">
        <button
          onClick={onSubmit}
          disabled={submitting}
          className="p-4 rounded-xl bg-blue-600 w-full mt-4 ml-4 mr-4 mb-2 font-bold text-white disabled:opacity-60"
        >
          {submitting ? (
            t("user.feedback.submitting")
          ) : (
            <>
              {t("user.feedback.submitToStaff")} <span className="m-2">➤</span>
            </>
          )}
        </button>
      </div>

      {/* ===== Modal เลือกตำแหน่งทางปิด ===== */}
      {pickerOpen && (
        <ClosurePickerMap
          defaultBuildingId={searchParams.get("buildingId") || undefined}
          defaultFloorId={searchParams.get("floorId") || undefined}
          onCancel={() => setPickerOpen(false)}
          onConfirm={(data) => {
            setClosure(data);
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}